/**
 * The household's own list of what it buys.
 *
 * This is where the two questions adding used to ask now live: where a food goes, and how long
 * it keeps. Keeping them on the food rather than in the add form is what makes adding one tap
 * — a household buys the same forty things and puts each one in the same place every time.
 *
 * The seeded catalog is not replaced by this, it is demoted. It supplies the first draft of an
 * entry, and after that the household's answer is the one that counts, because it is the one
 * somebody can go and correct.
 */

import type {
  Food,
  FoodLookup,
  Grocery,
  InventoryItem,
  Location,
  LocationKind,
  NewGrocery,
} from '../api/types';
import { resolveFood } from './catalog';
import { daysIn } from './expiry';

/** Find the entry a typed name refers to, by display name or by slug. */
export function resolveGrocery(groceries: Grocery[], name: string): Grocery | null {
  const wanted = normalize(name);

  return (
    groceries.find(
      (grocery) => grocery.slug === wanted || normalize(grocery.name) === wanted,
    ) ?? null
  );
}

/**
 * What is already in the kitchen that a corrected entry should take with it.
 *
 * Where an entry points is nearly always a guess — the catalog drafts it, or a model does —
 * so somebody changing it on the Groceries screen is nearly always fixing a wrong answer
 * rather than announcing that the food has moved house. Leaving the stock behind makes the app
 * insist the drinks are in the freezer, and worse, hides them from the next add: a restock
 * matches on location as well as on food, so the older rows are invisible and the arrival
 * quietly becomes yet another row.
 *
 * Only what sat in the place the entry used to name moves. Anything somebody has since put
 * somewhere else by hand stays where they put it, because that was a decision and this is a
 * correction — and the whole move is one undo away regardless.
 */
export function stockToFollow(
  items: InventoryItem[],
  slug: string,
  from: string,
): InventoryItem[] {
  return items.filter(
    (item) => item.food_ref === slug && item.location_id === from && item.state !== 'gone',
  );
}

/**
 * Whether this food tracks level only, with no expiry to nudge about.
 *
 * An entry nobody could place is not a staple, whatever its empty shelf life looks like: it is
 * a food we know nothing about, and calling it a staple would suppress the very nudges that
 * would remind somebody to fill it in.
 */
export function isStaple(grocery: Grocery): boolean {
  return grocery.shelf_life_days === null && grocery.source !== 'unknown';
}

/** Whether the app should be asking somebody to look at this entry. */
export function needsALook(grocery: Grocery): boolean {
  return grocery.source === 'unknown';
}

/**
 * An entry built from what the API was able to find out.
 *
 * The server has already decided whether it knows this food; all that is left is turning the
 * kind of place it named into one of the household's own locations.
 */
export function groceryFromLookup(
  found: FoodLookup,
  locations: Location[],
  slug: string,
): Grocery | null {
  if (locations.length === 0) {
    return null;
  }

  return {
    slug,
    name: found.name,
    location_id: placeOfKind(locations, found.storage).id,
    shelf_life_days: found.shelf_life_days,
    opened_shelf_life_days: found.opened_shelf_life_days,
    source: found.source,
  };
}

/**
 * The first draft of an entry for a food the household has not listed yet.
 *
 * Every field is guessed rather than asked for, which is the rule this whole design rests on:
 * a new field either has an inferred default or it does not ship. The catalog supplies the
 * shelf life and the kind of place the food belongs in; an unrecognised food — "leftover
 * curry" — lands in the first place the household defined with no expiry at all, which is the
 * honest answer and one tap away from being corrected.
 */
export function draftGrocery(
  name: string,
  catalog: Food[],
  locations: Location[],
): NewGrocery | null {
  const wanted = name.trim();
  if (wanted === '' || locations.length === 0) {
    return null;
  }

  const food = resolveFood(catalog, wanted);
  const where = homeFor(food, locations);

  return {
    name: wanted,
    location_id: where.id,
    slug: food?.slug ?? slugify(wanted),
    // A food the catalog placed is as settled as one the household typed. A food it could not
    // is not yet an answer at all — the caller asks the API next, and only records `unknown`
    // when that comes back with nothing either.
    source: food === null ? 'unknown' : 'catalog',
    ...shelfLifeOf(food, where.kind),
  };
}

/** The identity a newly named grocery gets, matching the seeded catalog's shape of slug. */
export function slugify(name: string): string {
  return name
    .split(/[^A-Za-z0-9]+/)
    .filter((word) => word !== '')
    .map((word) => word.toLowerCase())
    .join('-');
}

/**
 * Where a food belongs, out of the places this household actually has.
 *
 * The catalog says which kind of storage a food keeps in; this turns that into one of the
 * household's own locations, which is what an item needs. A garage freezer counts as a
 * freezer, so the answer stays right for households that named their places themselves.
 *
 * The fallback is the pantry, deliberately. It used to be `locations[0]`, which sounds
 * neutral and is not: locations come back in key order, so for the seeded household the first
 * one is the *freezer* — every food the catalog had never heard of went into the freezer and
 * was stamped with a frozen-on date. Dry storage is the harmless guess.
 */
function homeFor(food: Food | null, locations: Location[]): Location {
  return placeOfKind(locations, food === null ? 'pantry' : longestKeeping(food));
}

/** The household's own location of a given kind, falling back to dry storage. */
export function placeOfKind(locations: Location[], kind: LocationKind): Location {
  const wanted = locations.find((location) => location.kind === kind);
  const dryStorage = locations.find((location) => location.kind === 'pantry');

  // The final fallback only matters for a household that defined neither — one that has, say,
  // a fridge and nothing else. Somewhere real beats nowhere.
  return wanted ?? dryStorage ?? (locations[0] as Location);
}

/**
 * The kind of place this food belongs in.
 *
 * The freezer is not a candidate when anywhere else will do: it is where food goes to be
 * forgotten, and somebody adding chicken through quick-add is telling us about tonight rather
 * than about February. But when the freezer is the *only* place the catalog gives a shelf life
 * for — ice cream, frozen peas — it is the honest answer, and the alternative was drafting
 * them into a pantry that would never expire them.
 */
function longestKeeping(food: Food): LocationKind {
  const { fridge, pantry, freezer } = food.shelf_life_days;

  if (fridge !== null && (pantry === null || fridge > pantry)) {
    return 'fridge';
  }
  if (pantry === null && freezer !== null) {
    return 'freezer';
  }

  return 'pantry';
}

function shelfLifeOf(
  food: Food | null,
  kind: LocationKind,
): Pick<NewGrocery, 'shelf_life_days' | 'opened_shelf_life_days'> {
  // A catalog staple keeps for years and is never nudged about, so it arrives here as a food
  // with no shelf life at all — the same thing this list means by "it does not go off".
  if (food === null || food.staple) {
    return {};
  }

  const keeps = daysIn(food.shelf_life_days, kind);
  const opened =
    food.opened_shelf_life_days === null ? null : daysIn(food.opened_shelf_life_days, kind);

  return {
    ...(keeps === null ? {} : { shelf_life_days: keeps }),
    ...(opened === null ? {} : { opened_shelf_life_days: opened }),
  };
}

function normalize(name: string): string {
  return name.trim().toLowerCase().split(/\s+/).join(' ');
}
